import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ActionPermissions } from '../../../@types';
import { ServiceObject } from '../../../@types/service.type';
import { StructureAttribute } from '../../../@types/structure.type';
import { getPartByIdForService } from '../../../api/part';
import { ReportedInfoUpdateRequest } from '../../../api/services';
import { FaultCategory, NodeAttributeType, ServiceTypeCode } from '../../../configs/enums';
import { isUserHasPermission } from '../../../configs/permissions';
import { GetServiceKey } from '../../../configs/services';
import { useUpdateReportedInformation } from '../../../queries/service-query';
import { useGetFilteredNodeAttributesByCustomRange } from '../../../queries/structure-query';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { resolveModelForPartType } from '../../../utils/reported-model';
import { reportedFaultTypes } from '../../../utils/services';
import { toast } from '../../../utils/toast';

interface NodeAttributeItem {
  id: number | string;
  propertyTypeCode: string;
  value?: {
    clientModelName?: string;
    enabled?: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface ReportedInfoOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export type ReportedInfoToastSeverity = 'success' | 'error';

export interface ReportedInfoToast {
  severity: ReportedInfoToastSeverity;
  message: string;
}

export interface SerialPickResult {
  serial: string;
  partId: number;
}

export interface UseReportedInfoEditing {
  canEditReferences: boolean;
  canEditReportedInfo: boolean;
  canEditReportedSerial: boolean;
  reportedModelOptions: ReportedInfoOption[];
  reportedFaultOptions: ReportedInfoOption[];
  handleReportedInfoUpdate: (
    patch: ReportedInfoUpdateRequest,
    optimisticOverlay?: Partial<ServiceObject>
  ) => void;
  handleSerialPick: (result: SerialPickResult) => Promise<void>;
}

const REPORTED_INFO_ATTRIBUTE_TYPES = [
  NodeAttributeType.Models,
  NodeAttributeType.FaultCategory,
  NodeAttributeType.FaultCode,
];

// Rebuild the service with each overlay'd field rolled back to its snapshot
// value — but only when the cache still holds the value we wrote optimistically.
// If a newer edit has since replaced the field, we leave it alone so the
// later edit isn't clobbered by this rollback. Static key listing avoids
// dynamic bracket assignment (and the prototype-pollution warnings that come
// with it).
const safeRollback = (
  current: ServiceObject,
  overlay: Partial<ServiceObject>,
  snapshot: ServiceObject
): ServiceObject => {
  const result: ServiceObject = { ...current };
  if ('referenceNo1' in overlay && result.referenceNo1 === overlay.referenceNo1) {
    result.referenceNo1 = snapshot.referenceNo1;
  }
  if ('referenceNo2' in overlay && result.referenceNo2 === overlay.referenceNo2) {
    result.referenceNo2 = snapshot.referenceNo2;
  }
  if ('referenceNo3' in overlay && result.referenceNo3 === overlay.referenceNo3) {
    result.referenceNo3 = snapshot.referenceNo3;
  }
  if ('reportedModelId' in overlay && result.reportedModelId === overlay.reportedModelId) {
    result.reportedModelId = snapshot.reportedModelId;
  }
  if ('reportedModel' in overlay && result.reportedModel === overlay.reportedModel) {
    result.reportedModel = snapshot.reportedModel;
  }
  if (
    'reportedSerialNumber' in overlay &&
    result.reportedSerialNumber === overlay.reportedSerialNumber
  ) {
    result.reportedSerialNumber = snapshot.reportedSerialNumber;
  }
  if (
    'reportedSerialNumberPartId' in overlay &&
    result.reportedSerialNumberPartId === overlay.reportedSerialNumberPartId
  ) {
    result.reportedSerialNumberPartId = snapshot.reportedSerialNumberPartId;
  }
  if (
    'reportedFaultAndDescriptionId' in overlay &&
    result.reportedFaultAndDescriptionId === overlay.reportedFaultAndDescriptionId
  ) {
    result.reportedFaultAndDescriptionId = snapshot.reportedFaultAndDescriptionId;
  }
  if (
    'reportedFaultAndDescription' in overlay &&
    result.reportedFaultAndDescription === overlay.reportedFaultAndDescription
  ) {
    result.reportedFaultAndDescription = snapshot.reportedFaultAndDescription;
  }
  return result;
};

export const useReportedInfoEditing = (
  serviceId: number,
  service: ServiceObject | null,
  onSuccess?: () => void
): UseReportedInfoEditing => {
  const permissions = useGetUserSystemPermissions().data;
  const canEditReferences = isUserHasPermission(
    ActionPermissions.Service_Edit_Service_Parameters_General,
    permissions
  );
  const canEditReportedInfo = isUserHasPermission(
    ActionPermissions.Service_Edit_Service_Parameters_Reported_Information,
    permissions
  );

  // Repair jobs target a specific physical part. Re-pointing serial after creation
  // would silently decouple the job from that part, so the workflow is to cancel
  // and create a new job. Every other job type allows changing the reported serial
  // via the part-search popover — the model is derived from the chosen part rather
  // than being independently editable, so the model field is always read-only on
  // this panel.
  const isRepairJob = service?.serviceTypeCode === ServiceTypeCode.Repair;
  const canEditReportedSerial = canEditReportedInfo && !isRepairJob;

  const optionsQuery = useGetFilteredNodeAttributesByCustomRange(
    service?.parentId ?? '',
    REPORTED_INFO_ATTRIBUTE_TYPES,
    service?.serviceTypeCode ?? '',
    FaultCategory.Reported,
    service?.actionTypeCode ?? ''
  );

  useEffect(() => {
    if (service?.parentId && service?.serviceTypeCode) {
      optionsQuery.refetch();
    }
  }, [service?.parentId, service?.serviceTypeCode, service?.actionTypeCode]);

  const rawModelAttributes = useMemo<NodeAttributeItem[]>(() => {
    const items = (optionsQuery.data ?? []) as NodeAttributeItem[];
    return items.filter((item) => item.propertyTypeCode === NodeAttributeType.Models);
  }, [optionsQuery.data]);

  const reportedModelOptions = useMemo<ReportedInfoOption[]>(
    () =>
      rawModelAttributes.map((item) => ({
        label: item.value?.clientModelName ?? '',
        value: String(item.id),
        disabled: !item.value?.enabled,
      })),
    [rawModelAttributes]
  );

  const reportedFaultOptions = useMemo<ReportedInfoOption[]>(() => {
    const items = (optionsQuery.data ?? []) as NodeAttributeItem[];
    const faultItems = items.filter(
      (item) =>
        item.propertyTypeCode === NodeAttributeType.FaultCategory ||
        item.propertyTypeCode === NodeAttributeType.FaultCode
    );
    return reportedFaultTypes(faultItems as unknown as StructureAttribute[]).map((opt) => ({
      label: String(opt.label ?? ''),
      value: String(opt.value ?? ''),
    }));
  }, [optionsQuery.data]);

  const updateReportedInfoMutation = useUpdateReportedInformation();
  const queryClient = useQueryClient();

  // Monotonic counter for in-flight serial→part lookups. If the user picks a
  // second serial before the first lookup resolves, the older request must
  // not overwrite state with a stale model.
  const partLookupSeqRef = useRef(0);

  const handleReportedInfoUpdate = useCallback(
    (patch: ReportedInfoUpdateRequest, optimisticOverlay?: Partial<ServiceObject>) => {
      if (!serviceId) return;
      const key = [GetServiceKey, serviceId];
      // Fall back to the in-scope service prop when the cache has been evicted
      // (e.g., after garbage collection) so a save still goes through.
      const snapshot = queryClient.getQueryData<ServiceObject>(key) ?? service;
      if (!snapshot) return;

      if (optimisticOverlay) {
        queryClient.setQueryData<ServiceObject>(key, { ...snapshot, ...optimisticOverlay });
      }

      // Always send the full reported-info payload — the BFF treats omitted fields as cleared,
      // so editing one field without resending the others would wipe them.
      const current = queryClient.getQueryData<ServiceObject>(key) ?? snapshot;
      const fullPayload: ReportedInfoUpdateRequest = {
        referenceNo1: current.referenceNo1 ?? '',
        referenceNo2: current.referenceNo2 ?? '',
        referenceNo3: current.referenceNo3 ?? '',
        // The BFF field name is misleading: `reportedSerialNumber` accepts the numeric
        // part ID, not the serial string. Sending the partId matches the legacy fragment.
        reportedSerialNumber: current.reportedSerialNumberPartId,
        reportedFaultAndDescriptionId: current.reportedFaultAndDescriptionId,
        reportedModelId: current.reportedModelId,
        // Spread the caller's patch last so the explicit change always wins over the
        // cache-derived baseline, even if the optimistic overlay was skipped.
        ...patch,
      };

      updateReportedInfoMutation.mutate(
        { id: serviceId, ...fullPayload },
        {
          onSuccess: () => {
            toast.success('Saved');
            onSuccess?.();
          },
          onError: () => {
            if (optimisticOverlay) {
              queryClient.setQueryData<ServiceObject | undefined>(key, (old) =>
                old ? safeRollback(old, optimisticOverlay, snapshot) : old
              );
            }
            toast.error('Could not save — change reverted');
          },
        }
      );
    },
    [serviceId, service, updateReportedInfoMutation, onSuccess, queryClient]
  );

  const handleSerialPick = useCallback(
    async ({ serial, partId }: SerialPickResult): Promise<void> => {
      const baseOverlay: Partial<ServiceObject> = {
        reportedSerialNumber: serial,
        reportedSerialNumberPartId: partId || 0,
      };
      // Bump the sequence even on the no-partId branch so any in-flight lookup
      // from a prior pick is treated as stale and can't overwrite the cleared
      // state when it eventually resolves.
      const seq = ++partLookupSeqRef.current;
      // Cleared serial / no partId: no part to look up. Model is auto-derived
      // from the serial on this panel (read-only), so a cleared serial means
      // the model is no longer valid — clear both together to avoid the
      // inconsistent "no serial, ghost model" state.
      if (!partId) {
        handleReportedInfoUpdate(
          { reportedSerialNumber: 0, reportedModelId: 0 },
          { ...baseOverlay, reportedModelId: 0, reportedModel: '' }
        );
        return;
      }
      try {
        const part = await getPartByIdForService(partId);
        // Drop stale responses: if a newer pick fired in the meantime, that
        // one owns the final state.
        if (seq !== partLookupSeqRef.current) return;
        const partTypeId = (part?.partType as { id?: number } | undefined)?.id;
        const matchedModel = resolveModelForPartType(rawModelAttributes, partTypeId);
        if (matchedModel) {
          handleReportedInfoUpdate(
            {
              reportedSerialNumber: partId,
              reportedModelId: Number(matchedModel.id),
            },
            {
              ...baseOverlay,
              reportedModelId: Number(matchedModel.id),
              reportedModel: matchedModel.value?.clientModelName ?? '',
            }
          );
        } else {
          handleReportedInfoUpdate({ reportedSerialNumber: partId }, baseOverlay);
        }
      } catch {
        if (seq !== partLookupSeqRef.current) return;
        // Fall back to the un-derived patch on lookup failure rather than
        // blocking the serial change itself. Log stable identifiers only —
        // the error object may carry BFF-side data (part record, location)
        // that should not land in the browser console; richer details belong
        // in a sanitised telemetry path if/when one is wired up.
        console.error('Serial→Model lookup failed', { partId, seq });
        handleReportedInfoUpdate({ reportedSerialNumber: partId }, baseOverlay);
      }
    },
    [handleReportedInfoUpdate, rawModelAttributes]
  );

  return {
    canEditReferences,
    canEditReportedInfo,
    canEditReportedSerial,
    reportedModelOptions,
    reportedFaultOptions,
    handleReportedInfoUpdate,
    handleSerialPick,
  };
};
